import { isCloudId } from '../../contracts/src/providers.ts';
import type { AddonInstallation, Catalog, CloudFile, CloudId, HomeHeroPreferences, LibraryItem, Meta } from '../../contracts/src/index.ts';
import { homeHeroPreferences, sameHomeHeroPreferences, validHomeHeroPreferences } from '../../contracts/src/home-hero.ts';
import { CINEMETA_ID, cinemeta } from '../../contracts/src/cinemeta.ts';
import type { Api, RequestOptions } from './api.ts';
import { record, uuid } from './protocol.ts';
import { SlcError } from './types.ts';
import type { WatchMark, WatchTitle, HistoryPage } from '../../contracts/src/watched.ts';
import { metadataDetails, metadataHttps, metadataProgramme, playbackMetadata, embeddedVideoSource, type TrailerSource } from '../../contracts/src/metadata.ts';
import { nextEpisode } from '../../contracts/src/episodes.ts';
import { addonResourceSupported } from '../../contracts/src/addons.ts';
import { subtitleLookupNotice } from '../../contracts/src/subtitle-status.ts';
import type { EpisodePreference, PlaybackItem, PreparedEpisode } from './episodes.ts';

export function offers(addon: AddonInstallation, resource: 'stream' | 'meta', type: string, id: string) {
  return addon.enabled && !addon.manifest.behaviorHints?.configurationRequired &&
    addonResourceSupported(addon.manifest, resource, type, id);
}

const invalid = () => new SlcError('INVALID_CONTENT', 'This content could not be read. Refresh and try again.');
const retired = () => new SlcError('REQUEST_CANCELLED', 'This request was cancelled.');
const text = (value: unknown, max: number) => typeof value === 'string' && value.length <= max ? value : undefined;
function segment(value: unknown, max = 180): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= max && !/[\u0000-\u001f]/.test(value) && value !== '.' && value !== '..';
}
const artwork = metadataHttps;
const strings = (value: unknown, limit: number, max: number) => Array.isArray(value)
  ? value.slice(0, limit).filter((item): item is string => text(item, max) !== undefined) : undefined;
function publicCatalog(value: unknown): Catalog | undefined {
  if (!record(value) || !segment(value.id) || !segment(value.type, 40)) return;
  return { id: value.id, type: value.type, name: text(value.name, 4000),
    extraSupported: strings(value.extraSupported, 40, 180), extraRequired: strings(value.extraRequired, 40, 180),
    extra: Array.isArray(value.extra) ? value.extra.slice(0, 40).filter(record).filter(e => segment(e.name)).map(e => ({
      name: String(e.name), isRequired: e.isRequired === true, options: strings(e.options, 2000, 4000),
      optionsLimit: Number.isInteger(e.optionsLimit) && Number(e.optionsLimit) > 0 ? Number(e.optionsLimit) : undefined,
    })) : undefined };
}
/** Bound and project API metadata; do not copy arbitrary addon/provider fields
 * into TV view models or preserve credentials hidden in artwork user-info. */
export function contentMeta(value: unknown): Meta {
  if (!record(value) || !segment(value.id) || !segment(value.type, 40) || !text(value.name, 300)) throw invalid();
  return { ...metadataDetails(value), id: value.id, type: value.type, name: value.name as string,
    ...(uuid(value.sourceAddonId) ? { sourceAddonId: value.sourceAddonId } : {}),
    poster: artwork(value.poster), background: artwork(value.background), description: text(value.description, 20000),
    releaseInfo: text(value.releaseInfo, 100), runtime: text(value.runtime, 100), imdbRating: text(value.imdbRating, 10),
    genres: Array.isArray(value.genres) ? value.genres.filter((g): g is string => !!text(g, 100)).slice(0, 60) : undefined,
    videos: Array.isArray(value.videos) ? value.videos.slice(0, 8000).filter(record).filter(v => segment(v.id)).map(v => ({
      ...metadataProgramme(v), id: String(v.id), title: text(v.title, 4000), released: text(v.released, 80), thumbnail: artwork(v.thumbnail), overview: text(v.overview, 4000),
      season: Number.isInteger(v.season) ? Number(v.season) : undefined, episode: Number.isInteger(v.episode) ? Number(v.episode) : undefined,
      ...(v.embeddedStreams === true ? { embeddedStreams: true as const } : {}),
      ...(v.embeddedTrailers === true ? { embeddedTrailers: true as const } : {}),
    })) : undefined };
}
function metadataList(value: unknown, limit: number): Meta[] {
  if (!Array.isArray(value) || value.length > limit) throw invalid();
  const seen = new Set<string>();
  return value.flatMap(item => {
    try { const m = contentMeta(item), key = JSON.stringify([m.type, m.id]); if (seen.has(key)) return []; seen.add(key); return [m]; }
    catch { return []; }
  });
}
export type LibraryPage = { items: LibraryItem[]; nextCursor: string | null };
export type LibraryState = { saved: false; watched: null } | { saved: true; watched: boolean };
export type CalendarRelease = { mediaId: string; mediaType: string; name: string; videoId: string; title?: string;
  season?: number; episode?: number; released: string; day: string };
export type TvSource = { index: number; name: string; description: string; kind: 'torrent' | 'usenet' | 'direct' | 'youtube' | 'external';
  infoHash?: string; filename?: string; requiresHeaders: boolean; notWebReady: boolean; continuityKey?: string };
export type SourceBatch = { sources: TvSource[]; expiresAt: number; truncated: boolean };
export type SourceResolution = { status: 'sent'; sessionId: string; subtitleNotice?: string } | { status: 'choose_file'; files: CloudFile[] } | { status: 'preparing'; progress: number };
export type SourceCache = { providerId: string; providerName: string; cache: Record<string, 'cached' | 'uncached' | 'unknown'> };
/** First-party, owner-confirmed movie file; never an addon installation or URL. */
export type LinkedCloudSource = Readonly<{ kind: 'linked-cloud'; id: string; revision: number; movieId: string;
  providerId: string; providerName: string; name: string; size: number; state: 'linked' | 'relink-required' }>;
type Scope = { deviceId: string; householdId: string; profileId: string; providerId?: string };
// TV clients supply their device bearer. Desktop IPC keeps the bearer in the
// native vault and supplies only public enrollment identity to this policy.
type ContentIdentity = { read(): Promise<{ enrollment?: { deviceId: string; householdId: string; credential?: string } }> };
const infoHash = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f]{40}$/i.test(value);
const fileId = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) >= 0;
const timestamp = (v: unknown): v is number => Number.isSafeInteger(v) && Number(v) >= 0 && Number(v) <= 8_640_000_000_000_000;
const watchOrigin = (v: unknown): v is WatchMark['origin'] => v === 'manual' || v === 'playback' || v === 'trakt';
function watchTitle(meta: WatchTitle) {
  if (!segment(meta.id) || !segment(meta.type, 40) || !text(meta.name, 300)) throw invalid();
  return { id: meta.id, type: meta.type, name: meta.name };
}
function watchVideos(meta: Pick<Meta, 'id' | 'type'>, ids: string[]) {
  if (!segment(meta.id) || !segment(meta.type, 40) || !Array.isArray(ids) || !ids.length || ids.length > 2000 ||
      !ids.every(id => segment(id) && (meta.type !== 'movie' || id === meta.id))) throw invalid();
  return [...new Set(ids)];
}

/** Device-scoped content transport. The backend remains authoritative. There is
 * no arbitrary host/path request, profile picker, saved key or addon transport
 * URL in this interface. Rebinding/unlinking cancels the old profile's reads. */
export class TvContent {
  private api: Api;
  private identity: ContentIdentity;
  private expected: { deviceId: string; householdId: string };
  private scope: Scope | undefined;
  private epoch = 0;
  private active = new Set<AbortController>();
  private settings: Record<string, unknown> = {};
  private selections = new WeakMap<SourceBatch, { selection: string; epoch: number; expiresAt: number; addonId: string; episodes: boolean }>();
  private linkedGeneration = 0;
  private linkedSelections = new WeakMap<LinkedCloudSource, { epoch: number; generation: number; id: string; revision: number; state: LinkedCloudSource['state'] }>();
  constructor(api: Api, identity: ContentIdentity, expected: { deviceId: string; householdId: string }) {
    if (!uuid(expected.deviceId) || !uuid(expected.householdId)) throw invalid();
    this.api = api; this.identity = identity; this.expected = { ...expected };
  }
  bind(info: unknown): string {
    if (!record(info) || !record(info.device) || !record(info.profile) || !record(info.household) ||
        info.device.id !== this.expected.deviceId || info.device.householdId !== this.expected.householdId ||
        info.household.id !== this.expected.householdId || info.profile.householdId !== this.expected.householdId ||
        !uuid(info.profile.id) || info.device.profileId !== info.profile.id ||
        (info.device.providerConnectionId !== undefined && !uuid(info.device.providerConnectionId))) { this.clear(); throw invalid(); }
    const providerId = info.device.providerConnectionId as string | undefined;
    if (this.scope?.profileId !== info.profile.id || this.scope?.providerId !== providerId) {
      this.clear(); this.scope = { ...this.expected, profileId: info.profile.id, providerId };
    }
    this.settings = record(info.profile.settings) ? info.profile.settings : {};
    return info.profile.id;
  }
  episodeScope() { return this.scope ? JSON.stringify([this.scope.deviceId, this.scope.householdId, this.scope.profileId, this.scope.providerId || '']) : ''; }
  autoplay() { return this.settings.autoplay === true; }
  autoplayDelay() { return this.settings.autoplayDelaySeconds; }
  homeHero() { return homeHeroPreferences(this.settings.homeHero); }
  async saveHomeHero(preferences: HomeHeroPreferences, signal?: AbortSignal): Promise<HomeHeroPreferences> {
    if (!validHomeHeroPreferences(preferences)) throw invalid();
    const scope = this.scope, before = this.homeHero(), requested = { ...preferences };
    if (!scope) throw new SlcError('CONNECTING', 'Wait for this TV to connect.');
    const result = await this.request('home-hero', { method: 'PATCH', body: { expectedProfileId: scope.profileId, homeHero: requested }, signal }, 'receiver');
    if (this.scope !== scope || signal?.aborted) throw retired();
    if (!record(result) || result.saved !== true || result.profileId !== scope.profileId ||
        !validHomeHeroPreferences(result.homeHero) || !sameHomeHeroPreferences(result.homeHero, requested)) throw invalid();
    const current = this.homeHero();
    // A refresh carrying another change must win over an older acknowledgement.
    // The server may also have broadcast this exact save before replying.
    if (!sameHomeHeroPreferences(current, before) && !sameHomeHeroPreferences(current, requested)) throw retired();
    const saved = { ...result.homeHero };
    this.settings = { ...this.settings, homeHero: saved };
    return { ...saved };
  }
  clear() { this.epoch++; this.scope = undefined; this.settings = {}; for (const request of this.active) request.abort(); this.active.clear(); }
  private async request(route: string, options: RequestOptions = {}, device: boolean | 'receiver' = false) {
    const scope = this.scope, epoch = this.epoch;
    if (!scope) throw new SlcError('CONNECTING', 'Wait for this TV to connect.');
    const abort = new AbortController(), cancel = () => abort.abort();
    this.active.add(abort); options.signal?.addEventListener('abort', cancel, { once: true });
    if (options.signal?.aborted) abort.abort();
    const current = () => { if (abort.signal.aborted || epoch !== this.epoch || this.scope !== scope) throw retired(); };
    try {
      current(); const auth = (await this.identity.read()).enrollment; current();
      if (!auth || auth.deviceId !== scope.deviceId || auth.householdId !== scope.householdId) throw new SlcError('DEVICE_REQUIRED', 'Reconnect this TV from your phone.');
      const path = device === 'receiver' ? `/receiver/${route}` : device ? `/devices/${scope.deviceId}/${route}` : `/profiles/${scope.profileId}/${route}`;
      const value = await this.api.request(path, { ...options, credential: auth.credential, signal: abort.signal });
      current(); return value;
    } finally { options.signal?.removeEventListener('abort', cancel); this.active.delete(abort); }
  }
  async library(cursor: string | null = null, signal?: AbortSignal): Promise<LibraryPage> {
    const valid = (c: unknown): c is string => typeof c === 'string' && /^[A-Za-z0-9_-]{1,2048}$/.test(c);
    if (cursor !== null && !valid(cursor)) throw invalid();
    const value = await this.request(`library${cursor ? '?cursor=' + encodeURIComponent(cursor) : ''}`, { signal });
    if (!record(value) || !Array.isArray(value.items) || value.items.length > 200 ||
        (value.nextCursor !== null && (!valid(value.nextCursor) || value.nextCursor === cursor))) throw invalid();
    const items: LibraryItem[] = value.items.flatMap(row => {
      if (!record(row)) return [];
      try { return [{ meta: contentMeta(row.meta), addedAt: Number(row.addedAt) || 0, watched: row.watched === true,
        position: typeof row.position === 'number' && row.position >= 0 ? row.position : undefined,
        duration: typeof row.duration === 'number' && row.duration >= 0 ? row.duration : undefined,
        videoId: segment(row.videoId) ? row.videoId : undefined }]; } catch { return []; }
    });
    return { items, nextCursor: value.nextCursor as string | null };
  }
  async calendar(signal?: AbortSignal): Promise<CalendarRelease[]> {
    const value = await this.request('calendar', { signal });
    if (!Array.isArray(value) || value.length > 300) throw invalid();
    const seen = new Set<string>();
    return value.flatMap(row => {
      if (!record(row) || !segment(row.mediaId) || !segment(row.mediaType, 40) || !segment(row.videoId) ||
          !text(row.name, 300) || !text(row.released, 80)) return [];
      const released = String(row.released), day = released.slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !Number.isFinite(Date.parse(released)) ||
          new Date(`${day}T00:00:00Z`).toISOString().slice(0, 10) !== day) return [];
      const key = JSON.stringify([row.mediaType, row.mediaId, row.videoId]);
      if (seen.has(key)) return []; seen.add(key);
      return [{ mediaId: row.mediaId, mediaType: row.mediaType, videoId: row.videoId, name: String(row.name),
        released, day, title: text(row.title, 300), season: Number.isInteger(row.season) && Number(row.season) >= 0 ? Number(row.season) : undefined,
        episode: Number.isInteger(row.episode) && Number(row.episode) >= 0 ? Number(row.episode) : undefined }];
    }).sort((a, b) => a.day.localeCompare(b.day));
  }
  private libraryRoute(meta: Pick<Meta, 'id' | 'type'>) {
    if (!segment(meta.id) || !segment(meta.type, 40)) throw invalid();
    return `library-item?${new URLSearchParams({ type: meta.type, mediaId: meta.id })}`;
  }
  private libraryResult(value: unknown): LibraryState {
    if (record(value)) {
      if (value.saved === false && value.watched === null) return { saved: false, watched: null };
      if (value.saved === true && typeof value.watched === 'boolean') return { saved: true, watched: value.watched };
    }
    throw invalid();
  }
  async libraryState(meta: Pick<Meta, 'id' | 'type'>, signal?: AbortSignal): Promise<LibraryState> {
    return this.libraryResult(await this.request(this.libraryRoute(meta), { signal }));
  }
  async saveLibrary(meta: Meta, signal?: AbortSignal): Promise<void> {
    const clean = contentMeta(meta);
    // The library API's complete episode bound also protects calendar storage.
    // Never silently truncate an episode list when adding a title.
    if ((clean.videos?.length || 0) > 2000) throw new SlcError('LIBRARY_TOO_LARGE', 'This title has more episodes than the library currently supports.');
    if (clean.videos) clean.videos = clean.videos.map(video => ({ ...video, title: video.title?.slice(0, 300) }));
    const result = await this.request('library', { method: 'PUT', body: { meta: clean }, signal });
    if (!record(result) || result.saved !== true) throw invalid();
  }
  async setWatched(meta: Pick<Meta, 'id' | 'type'>, watched: boolean, signal?: AbortSignal): Promise<LibraryState> {
    if (typeof watched !== 'boolean') throw invalid();
    const result = this.libraryResult(await this.request(this.libraryRoute(meta), { method: 'PATCH', body: { watched }, signal }));
    if (!result.saved || result.watched !== watched) throw invalid();
    return result;
  }
  async removeLibrary(meta: Pick<Meta, 'id' | 'type'>, signal?: AbortSignal): Promise<void> {
    const result = await this.request(this.libraryRoute(meta), { method: 'DELETE', signal });
    if (!record(result) || result.removed !== true) throw invalid();
  }
  async watched(meta: Pick<Meta, 'id' | 'type'>, ids: string[], signal?: AbortSignal): Promise<WatchMark[]> {
    const videoIds = watchVideos(meta, ids);
    const result = await this.request('watched/query', { method: 'POST', body: { type: meta.type, mediaId: meta.id, videoIds }, signal });
    if (!record(result) || !Array.isArray(result.items) || result.items.length > videoIds.length) throw invalid();
    const seen = new Set<string>();
    return result.items.map(row => {
      if (!record(row) || !segment(row.videoId) || !videoIds.includes(row.videoId) || seen.has(row.videoId) ||
          typeof row.watched !== 'boolean' || !(row.watchedAt === null || timestamp(row.watchedAt)) ||
          !timestamp(row.updatedAt) || !watchOrigin(row.origin)) throw invalid();
      seen.add(row.videoId);
      return { videoId: row.videoId, watched: row.watched, watchedAt: row.watchedAt, updatedAt: row.updatedAt, origin: row.origin };
    });
  }
  async markVideos(meta: WatchTitle, ids: string[], watched: boolean, signal?: AbortSignal): Promise<void> {
    const title = watchTitle(meta), videoIds = watchVideos(meta, ids);
    if (typeof watched !== 'boolean') throw invalid();
    const result = await this.request('watched', { method: 'POST', body: { meta: title, videoIds, watched }, signal });
    if (!record(result) || result.saved !== true || result.watched !== watched || result.count !== videoIds.length) throw invalid();
  }
  async viewingHistory(cursor: string | null = null, signal?: AbortSignal): Promise<HistoryPage> {
    const valid = (v: unknown): v is string => typeof v === 'string' && /^[A-Za-z0-9_-]{1,4096}$/.test(v);
    if (cursor !== null && !valid(cursor)) throw invalid();
    const result = await this.request(`history${cursor ? '?cursor=' + encodeURIComponent(cursor) : ''}`, { signal });
    if (!record(result) || !Array.isArray(result.items) || result.items.length > 50 ||
        !(result.nextCursor === null || (valid(result.nextCursor) && result.nextCursor !== cursor))) throw invalid();
    return { nextCursor: result.nextCursor, items: result.items.map(row => {
      if (!record(row) || !record(row.meta) || !segment(row.videoId) || !(row.watchedAt === null || timestamp(row.watchedAt)) ||
          !timestamp(row.updatedAt) || !watchOrigin(row.origin)) throw invalid();
      return { meta: watchTitle(contentMeta(row.meta)), videoId: row.videoId, watchedAt: row.watchedAt, updatedAt: row.updatedAt, origin: row.origin };
    }) };
  }
  async searchHistory(signal?: AbortSignal): Promise<string[]> {
    const result = await this.request('search-history', { signal });
    if (!Array.isArray(result) || result.length > 30) throw invalid();
    const seen = new Set<string>();
    return result.filter(record).flatMap(row => {
      const query = text(row.query, 200)?.trim();
      if (!query || /[\u0000-\u001f]/.test(query) || seen.has(query)) return [];
      seen.add(query); return [query];
    });
  }
  async rememberSearch(query: string, signal?: AbortSignal): Promise<void> {
    if (typeof query !== 'string' || !query.trim() || query.trim().length > 200 || /[\u0000-\u001f]/.test(query)) throw invalid();
    const result = await this.request('search-history', { method: 'POST', body: { query: query.trim() }, signal });
    if (!record(result) || result.saved !== true) throw invalid();
  }
  async clearSearchHistory(signal?: AbortSignal): Promise<void> {
    const result = await this.request('search-history', { method: 'DELETE', signal });
    if (!record(result) || result.cleared !== true) throw invalid();
  }
  async continueWatching(signal?: AbortSignal): Promise<LibraryItem[]> {
    const value = await this.request('continue', { signal });
    if (!Array.isArray(value) || value.length > 30) throw invalid();
    return value.filter(record).flatMap(row => {
      try { if (!segment(row.videoId) || typeof row.position !== 'number' || row.position < 0 || typeof row.duration !== 'number' || row.duration < 0) return [];
        return [{ meta: contentMeta(row.meta), videoId: row.videoId, position: row.position, duration: row.duration,
          updatedAt: Number(row.updatedAt) || 0, addedAt: 0, watched: false }]; } catch { return []; }
    });
  }
  async addons(signal?: AbortSignal): Promise<AddonInstallation[]> {
    const value = await this.request('addons', { signal });
    if (!record(value) || !Array.isArray(value.addons) || value.addons.length > 24) throw invalid();
    return value.addons.filter(record).flatMap(row => {
      // Old or unreviewed responses cannot expose addon names/artwork or become
      // source candidates on receivers. The server independently enforces this.
      if (!record(row.policy) || row.policy.status !== 'allowed') return [];
      const m = row.manifest;
      if (!uuid(row.id) || !record(m) || !segment(m.id) || !text(m.name, 180) || !text(m.version, 80) ||
          !Array.isArray(m.types) || !Array.isArray(m.resources) || !Array.isArray(m.catalogs) || m.catalogs.length > 200) return [];
      // The API validates every manifest. Keep only its public protocol fields.
      const catalogs = m.catalogs.flatMap(c => { const catalog = publicCatalog(c); return catalog ? [catalog] : []; });
      const resources = m.resources.slice(0, 40).flatMap((r): AddonInstallation['manifest']['resources'] => {
        if (segment(r, 40)) return [r];
        if (!record(r) || !segment(r.name, 40)) return [];
        return [{ name: r.name, types: strings(r.types, 50, 40), idPrefixes: strings(r.idPrefixes, 100, 100) }];
      });
      return [{ id: row.id, enabled: row.enabled === true, position: Number(row.position) || 0, policy: { status: 'allowed' },
        manifest: { id: m.id, name: String(m.name), version: String(m.version), types: m.types.slice(0, 50).filter((t): t is string => segment(t, 40)),
          resources, catalogs, description: text(m.description, 4000), logo: artwork(m.logo),
          idPrefixes: strings(m.idPrefixes, 100, 100), behaviorHints: record(m.behaviorHints) ? {
            configurable: m.behaviorHints.configurable === true, configurationRequired: m.behaviorHints.configurationRequired === true, epgProvider: m.behaviorHints.epgProvider === true,
          } : undefined } }];
    });
  }
  async catalog(addonId: string, catalog: Pick<Catalog, 'id' | 'type'>, extra: Record<string, string> = {}, signal?: AbortSignal) {
    return (await this.catalogPage(addonId, catalog, extra, signal)).items;
  }
  async catalogPage(addonId: string, catalog: Pick<Catalog, 'id' | 'type'>, extra: Record<string, string> = {}, signal?: AbortSignal) {
    if ((!uuid(addonId) && addonId !== CINEMETA_ID) || !segment(catalog.id) || !segment(catalog.type, 40) || Object.keys(extra).length > 12 ||
        Object.entries(extra).some(([k, v]) => !segment(k) || typeof v !== 'string' || v.length > 2000)) throw invalid();
    const value = await this.request(addonId === CINEMETA_ID ? 'catalogs/cinemeta' : `addons/${addonId}/resource`, { method: 'POST', body: { resource: 'catalog', id: catalog.id, type: catalog.type, extra }, signal });
    if (!record(value)) throw invalid(); const items = metadataList(value.metas, 200);
    const received = value.received ?? (value.metas as unknown[]).length;
    if (!Number.isInteger(received) || Number(received) < (value.metas as unknown[]).length || Number(received) > 200) throw invalid();
    return { items, received: Number(received) };
  }
  async metadata(addonId: string, item: Pick<Meta, 'id' | 'type'>, signal?: AbortSignal): Promise<Meta | null> {
    if ((!uuid(addonId) && addonId !== CINEMETA_ID) || !segment(item.id) || !segment(item.type, 40)) throw invalid();
    const value = await this.request(addonId === CINEMETA_ID ? 'catalogs/cinemeta' : `addons/${addonId}/resource`, { method: 'POST', body: { resource: 'meta', id: item.id, type: item.type, extra: {} }, signal });
    if (!record(value)) throw invalid(); if (value.meta === null) return null;
    const meta = contentMeta(value.meta); if (meta.id !== item.id || meta.type !== item.type) throw invalid(); return meta;
  }
  async sources(addonId: string, meta: Meta, videoId: string, signal?: AbortSignal, purpose?: TrailerSource): Promise<SourceBatch> {
    if (!uuid(addonId) || !segment(videoId)) throw invalid();
    const embedded = purpose ? undefined : embeddedVideoSource(meta, videoId);
    if (purpose === 'trailer' && (meta.trailerSourceAddonId !== addonId || videoId !== meta.id) ||
        purpose === 'video-trailer' && (meta.sourceAddonId !== addonId || !meta.videos?.some(v => v.id === videoId && v.embeddedTrailers))) throw new SlcError('METADATA_SOURCE_CHANGED', 'Refresh this title to use its own trailer sources.');
    if (embedded && embedded.addonId !== addonId) throw new SlcError('METADATA_SOURCE_CHANGED', 'Refresh this title to use its own addon sources.');
    const summary = playbackMetadata(contentMeta(meta)), epoch = this.epoch;
    const value = await this.request(`addons/${addonId}/sources`, { method: 'POST', body: { meta: summary, videoId, ...(purpose ? { source: purpose } : embedded ? { source: 'metadata' } : {}) }, signal });
    if (!record(value) || !text(value.selection, 180000) || !Number.isSafeInteger(value.expiresAt) ||
        Number(value.expiresAt) <= Date.now() || Number(value.expiresAt) > Date.now() + 300000 ||
        !Array.isArray(value.sources) || value.sources.length > 300) throw invalid();
    const seen = new Set<number>();
    const sources = value.sources.map((s): TvSource => {
      if (!record(s) || !Number.isInteger(s.index) || Number(s.index) < 0 || Number(s.index) > 299 || seen.has(Number(s.index)) ||
          !text(s.name, 4000) || text(s.description, 20000) === undefined || !['torrent', 'usenet', 'direct', 'youtube', 'external'].includes(String(s.kind)) ||
          (s.infoHash !== undefined && !infoHash(s.infoHash))) throw invalid();
      seen.add(Number(s.index));
      return { index: Number(s.index), name: String(s.name), description: String(s.description), kind: s.kind as TvSource['kind'],
        infoHash: typeof s.infoHash === 'string' ? s.infoHash.toLowerCase() : undefined, filename: text(s.filename, 4000),
        ...(typeof s.continuityKey === 'string' && /^[A-Za-z0-9_-]{43}$/.test(s.continuityKey) ? { continuityKey: s.continuityKey } : {}),
        requiresHeaders: s.requiresHeaders === true, notWebReady: s.notWebReady === true };
    });
    const batch = { sources, expiresAt: Number(value.expiresAt), truncated: value.truncated === true };
    // The sealed, actor-bound capability is held privately, never in a poster or
    // source view model. Old profile/provider batches cannot be reused locally;
    // the backend independently rechecks actor, grants, expiry and provider.
    this.selections.set(batch, { selection: String(value.selection), epoch, expiresAt: batch.expiresAt, addonId,
      episodes: !purpose && meta.type === 'series' }); return batch;
  }
  episodePreference(batch: SourceBatch, index: number): EpisodePreference | undefined {
    const capability = this.selections.get(batch), source = batch.sources.find(s => s.index === index);
    if (!capability?.episodes || capability.epoch !== this.epoch || capability.expiresAt <= Date.now() || !source?.continuityKey) return;
    return { scope: this.episodeScope(), addonId: capability.addonId, continuityKey: source.continuityKey };
  }
  async episodeMetadata(item: PlaybackItem, signal: AbortSignal, installed?: AddonInstallation[]): Promise<Meta | null> {
    const addons = [...(installed || await this.addons(signal)), cinemeta()].filter(a => offers(a, 'meta', item.type, item.mediaId));
    for (const addon of addons) {
      if (signal.aborted) throw retired();
      try {
        const meta = await this.metadata(addon.id, { id: item.mediaId, type: item.type }, signal);
        if (meta?.videos?.length) return meta;
      } catch { if (signal.aborted) throw retired(); }
    }
    return null;
  }
  /** The caller refreshes /receiver first. Re-read metadata, installation and
   * availability; the authenticated resolver independently enforces cachedOnly. */
  async continueEpisode(next: PreparedEpisode, preference: EpisodePreference, requestId: string, signal: AbortSignal): Promise<boolean> {
    const current = () => {
      if (signal.aborted || !this.scope || next.scope !== this.episodeScope() || preference.scope !== next.scope || !this.autoplay()) throw retired();
    };
    current();
    if (!uuid(requestId) || !uuid(next.item.sessionId) || !/^[A-Za-z0-9_-]{43}$/.test(preference.continuityKey)) throw invalid();
    const addons = await this.addons(signal); current();
    const meta = await this.episodeMetadata(next.item, signal, addons); current();
    if (!meta) return false;
    const candidate = nextEpisode(meta, next.item.videoId);
    if (candidate.status !== 'ready' || candidate.video.id !== next.video.id) return false;
    const addon = addons.find(a => a.id === preference.addonId), embedded = embeddedVideoSource(meta, next.video.id);
    if (!addon?.enabled || addon.manifest.behaviorHints?.configurationRequired ||
        (embedded ? embedded.addonId !== addon.id : !offers(addon, 'stream', meta.type, next.video.id))) return false;
    const batch = await this.sources(addon.id, meta, next.video.id, signal); current();
    const words = (key: string) => Array.isArray(this.settings[key]) ? (this.settings[key] as unknown[]).filter((v): v is string => typeof v === 'string').map(v => v.toLowerCase()) : [];
    const include = words('includeKeywords'), exclude = words('excludeKeywords');
    const candidates = batch.sources.filter(source => {
      const label = `${source.name} ${source.description}`.toLowerCase();
      return source.continuityKey === preference.continuityKey && !source.requiresHeaders && !source.notWebReady &&
        !exclude.some(word => label.includes(word)) && (!include.length || include.some(word => label.includes(word)));
    });
    const hashes = [...new Set(candidates.flatMap(s => s.kind === 'torrent' && s.infoHash ? [s.infoHash] : []))];
    const cache: SourceCache['cache'] = {};
    if (this.scope?.providerId) for (let i = 0; i < hashes.length; i += 100) {
      Object.assign(cache, (await this.cache(hashes.slice(i, i + 100), signal)).cache); current();
    }
    const selected = candidates.find(s => s.kind === 'direct' || s.kind === 'torrent' && s.infoHash && cache[s.infoHash] === 'cached');
    if (!selected) return false;
    const capability = this.selections.get(batch); current();
    if (!capability || capability.epoch !== this.epoch || capability.expiresAt <= Date.now()) return false;
    const response = await this.request('resolve', { method: 'POST', signal, body: { selection: capability.selection, index: selected.index,
      deviceId: this.expected.deviceId, requestId, mode: 'continue', continuation: { sessionId: next.item.sessionId, providerId: this.scope!.providerId || null } } });
    return this.resolution(response, requestId).status === 'sent';
  }
  async cache(hashes: string[], signal?: AbortSignal): Promise<SourceCache> {
    if (!Array.isArray(hashes) || !hashes.length || hashes.length > 100 || hashes.some(h => !infoHash(h))) throw invalid();
    const requested = [...new Set(hashes.map(h => h.toLowerCase()))];
    const value = await this.request('cache', { method: 'POST', body: { hashes: requested }, signal }, true);
    if (!record(value) || !uuid(value.providerId) || !text(value.providerName, 180) || !record(value.cache)) throw invalid();
    if (this.scope?.providerId && this.scope.providerId !== value.providerId) throw new SlcError('PROVIDER_CHANGED', 'The TV’s provider changed. Refresh its sources.');
    const cache: SourceCache['cache'] = {};
    for (const hash of requested) {
      const entry = value.cache[hash];
      cache[hash] = record(entry) && ['cached', 'uncached'].includes(String(entry.state)) ? entry.state as 'cached' | 'uncached' : 'unknown';
    }
    return { providerId: value.providerId, providerName: String(value.providerName), cache };
  }
  private resolution(value: unknown, requestId: string): SourceResolution {
    if (!record(value)) throw invalid();
    if (value.status === 'sent' && value.deviceId === this.expected.deviceId && value.sessionId === requestId &&
        record(value.accepted) && value.accepted.accepted === true) {
      const subtitleNotice = subtitleLookupNotice(value);
      return { status: 'sent', sessionId: requestId, ...(subtitleNotice ? { subtitleNotice } : {}) };
    }
    if (value.status === 'preparing' && typeof value.progress === 'number' && Number.isFinite(value.progress) && value.progress >= 0 && value.progress <= 1)
      return { status: 'preparing', progress: value.progress };
    if (value.status === 'choose_file' && Array.isArray(value.files) && value.files.length <= 10000) {
      const seen = new Set<CloudId>();
      const files: CloudFile[] = value.files.filter(record).flatMap(f => {
          if (!isCloudId(f.id) || seen.has(f.id) || f.infected === true || !text(f.name, 4000) || !fileId(f.size) ||
              !/\.(mkv|mp4|avi|webm|mov|m4v|ts|m2ts|mpg|mpeg|wmv|m3u8|mpd)$/i.test(String(f.name))) return [];
          seen.add(f.id); return [{ id: f.id, name: String(f.name), size: f.size, shortName: text(f.shortName, 4000) }];
        });
      return { status: 'choose_file', files };
    }
    // Never accept a raw Load command, URL or external handoff in the browsing
    // path. Only the authenticated receiver socket can start its decoder.
    throw invalid();
  }
  async resolve(batch: SourceBatch, index: number, requestId: string, selectedFile?: CloudId, signal?: AbortSignal): Promise<SourceResolution> {
    const capability = this.selections.get(batch);
    if (!capability || capability.epoch !== this.epoch || capability.expiresAt <= Date.now())
      throw new SlcError('SOURCE_EXPIRED', 'Refresh the sources and choose one again.');
    if (!batch.sources.some(s => s.index === index) || !uuid(requestId) || (selectedFile !== undefined && !isCloudId(selectedFile))) throw invalid();
    const value = await this.request('resolve', { method: 'POST', body: { selection: capability.selection, index,
      deviceId: this.expected.deviceId, mode: 'play', requestId, ...(selectedFile === undefined ? {} : { fileId: selectedFile }) }, signal });
    return this.resolution(value, requestId);
  }
  async resumeCloud(mediaId: string, requestId: string, signal?: AbortSignal): Promise<SourceResolution> {
    if (!segment(mediaId) || !mediaId.startsWith('cloud:') || !uuid(requestId)) throw invalid();
    return this.resolution(await this.request('cloud/resume', { method: 'POST', body: { mediaId, deviceId: this.expected.deviceId, requestId }, signal }), requestId);
  }
  async linkedCloudTitles(meta: Pick<Meta, 'id' | 'type'>, signal?: AbortSignal): Promise<LinkedCloudSource[]> {
    if (meta.type !== 'movie' || !/^tt\d{5,12}$/.test(meta.id)) throw invalid();
    const scope = this.scope, epoch = this.epoch, generation = ++this.linkedGeneration;
    if (!scope) throw new SlcError('CONNECTING', 'Wait for this TV to connect.');
    if (!scope.providerId) return [];
    const query = new URLSearchParams({ movieId: meta.id, deviceId: scope.deviceId });
    const value = await this.request(`cloud-titles?${query}`, { signal });
    if (generation !== this.linkedGeneration) throw retired();
    if (!record(value) || !Array.isArray(value.links) || value.links.length > 24) throw invalid();
    const seen = new Set<string>();
    return value.links.map((link): LinkedCloudSource => {
      if (!record(link) || !uuid(link.id) || seen.has(link.id) || !Number.isSafeInteger(link.revision) || Number(link.revision) < 1 ||
          !record(link.movie) || link.movie.type !== 'movie' || link.movie.id !== meta.id || !text(link.movie.name, 300) ||
          link.providerId !== scope.providerId || !text(link.providerName, 180) || !record(link.file) ||
          !text(link.file.name, 4000) || !fileId(link.file.size) || typeof link.canManage !== 'boolean' ||
          (link.state !== 'linked' && link.state !== 'relink-required')) throw invalid();
      seen.add(link.id);
      const source: LinkedCloudSource = Object.freeze({ kind: 'linked-cloud', id: link.id, revision: Number(link.revision), movieId: meta.id,
        providerId: scope.providerId!, providerName: String(link.providerName), name: String(link.file.name), size: Number(link.file.size), state: link.state });
      this.linkedSelections.set(source, { epoch, generation, id: source.id, revision: source.revision, state: source.state });
      return source;
    });
  }
  async playLinkedCloudTitle(source: LinkedCloudSource, requestId: string, signal?: AbortSignal): Promise<SourceResolution> {
    const selected = this.linkedSelections.get(source);
    if (!selected || selected.epoch !== this.epoch || selected.generation !== this.linkedGeneration)
      throw new SlcError('CLOUD_TITLE_CHANGED', 'This linked file changed. Refresh sources and choose again.');
    if (selected.state !== 'linked') throw new SlcError('CLOUD_TITLE_RELINK_REQUIRED', 'Ask the provider owner to relink this movie from Streamit on their phone.');
    if (!uuid(requestId)) throw invalid();
    try {
      const value = await this.request(`cloud-titles/${selected.id}/play`, { method: 'POST',
        body: { revision: selected.revision, requestId, deviceId: this.expected.deviceId }, signal });
      if (selected.generation !== this.linkedGeneration) throw retired();
      if (!record(value) || value.status !== 'sent') throw invalid();
      return this.resolution(value, requestId);
    } catch (error) {
      if (error && typeof error === 'object' && 'code' in error && ['CLOUD_TITLE_CHANGED', 'CLOUD_TITLE_NOT_FOUND',
          'CLOUD_TITLE_RELINK_REQUIRED', 'FILE_CHANGED', 'FILE_UNAVAILABLE', 'PROVIDER_CHANGED', 'PROFILE_CHANGED',
          'PROVIDER_CONSENT_REQUIRED', 'DEVICE_ACCESS', 'PROFILE_ACCESS'].includes(String(error.code))) this.linkedGeneration++;
      throw error;
    }
  }
}
